import { useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectClinicProfile,
} from "../../store/slices/authSlice";
import {
  useGetClinicFilesQuery,
  useGetClinicFoldersQuery,
  useUploadClinicFileMutation,
  useUpdateClinicFileMutation,
  useDeleteClinicFileMutation,
  useCreateClinicFolderMutation,
  useUpdateClinicFolderMutation,
  useDeleteClinicFolderMutation,
  useGetStorageUsageQuery,
} from "../../store/services/clinicFilesApi";
import {
  MAX_FILE_SIZE_MB,
  getFileTypeCategory,
  isFilePreviewable,
} from "../../firebase";
import {
  Folder,
  FolderOpen,
  File,
  FilePdf,
  FileDoc,
  FileXls,
  Image,
  UploadSimple,
  Trash,
  PencilSimple,
  X,
  Check,
  Eye,
  DownloadSimple,
  CaretLeft,
  CloudArrowUp,
  FolderPlus,
  Warning,
  Info,
  Camera,
  ImageSquare,
  Printer,
} from "@phosphor-icons/react";
import { HelpButton } from "../shared";
import ModalCloseButton from "../ui/ModalCloseButton";
import EmptyDraw from "../ui/EmptyDraw";
import "./ClinicFilesTab.css";
import ContentLoader from "../shared/ContentLoader";
import LoaderLoop from "../shared/loader/LoaderLoop";

// File type icons
const FILE_ICONS = {
  image: Image,
  pdf: FilePdf,
  word: FileDoc,
  excel: FileXls,
  other: File,
};

// Folder colors
const FOLDER_COLORS = [
  "#6366f1", // Indigo
  "#8b5cf6", // Purple
  "#ec4899", // Pink
  "#ef4444", // Red
  "#f97316", // Orange
  "#eab308", // Yellow
  "#22c55e", // Green
  "#14b8a6", // Teal
  "#3b82f6", // Blue
  "#6b7280", // Gray
];

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

function ClinicFilesTab() {
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const clinicProfile = useSelector(selectClinicProfile);

  // State
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [showFilePreview, setShowFilePreview] = useState(null);
  const [editingFile, setEditingFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [folderForm, setFolderForm] = useState({ name: "", color: FOLDER_COLORS[0] });
  const [fileDescriptionForm, setFileDescriptionForm] = useState("");

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const printIframeRef = useRef(null);

  // Check if mobile device
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Queries
  const { data: files = [], isLoading: isLoadingFiles } = useGetClinicFilesQuery(
    { clinicId, folderId: selectedFolder },
    { skip: !clinicId }
  );

  const { data: folders = [], isLoading: isLoadingFolders } = useGetClinicFoldersQuery(
    { clinicId },
    { skip: !clinicId }
  );

  const { data: storageUsage } = useGetStorageUsageQuery(
    { clinicId },
    { skip: !clinicId }
  );

  // Mutations
  const [uploadFile, { isLoading: isUploading }] = useUploadClinicFileMutation();
  const [updateFile] = useUpdateClinicFileMutation();
  const [deleteFile, { isLoading: isDeleting }] = useDeleteClinicFileMutation();
  const [createFolder, { isLoading: isCreatingFolder }] = useCreateClinicFolderMutation();
  const [updateFolder] = useUpdateClinicFolderMutation();
  const [deleteFolder, { isLoading: isDeletingFolder }] = useDeleteClinicFolderMutation();

  // Handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      await handleFilesUpload(droppedFiles);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleFileSelect = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    await handleFilesUpload(selectedFiles);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";
  };

  const handleFilesUpload = async (filesToUpload) => {
    setUploadError(null);

    for (const file of filesToUpload) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setUploadError(`הקובץ ${file.name} גדול מדי. הגודל המקסימלי הוא ${MAX_FILE_SIZE_MB}MB`);
        continue;
      }

      try {
        await uploadFile({
          file,
          clinicId,
          therapistId,
          therapistName,
          folderId: selectedFolder,
        }).unwrap();
      } catch (error) {
        console.error("[CK-2320] Error uploading clinic file:", {
          errorCode: "CK-2320",
          errorMessage: error.message,
          fileName: file.name,
          timestamp: new Date().toISOString(),
        });
        setUploadError(error.message || "שגיאה בהעלאת הקובץ");
      }
    }
  };

  const handleCreateFolder = async () => {
    if (!folderForm.name.trim()) return;

    try {
      await createFolder({
        clinicId,
        folderData: {
          name: folderForm.name.trim(),
          color: folderForm.color,
        },
        therapistId,
        therapistName,
      }).unwrap();

      setShowFolderModal(false);
      setFolderForm({ name: "", color: FOLDER_COLORS[0] });
    } catch (error) {
      console.error("[CK-2321] Error creating clinic folder:", {
        errorCode: "CK-2321",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleUpdateFolder = async () => {
    if (!editingFolder || !folderForm.name.trim()) return;

    try {
      await updateFolder({
        clinicId,
        folderId: editingFolder.id,
        folderData: {
          name: folderForm.name.trim(),
          color: folderForm.color,
        },
      }).unwrap();

      setEditingFolder(null);
      setFolderForm({ name: "", color: FOLDER_COLORS[0] });
    } catch (error) {
      console.error("[CK-2322] Error updating clinic folder:", {
        errorCode: "CK-2322",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await deleteFolder({ clinicId, folderId }).unwrap();
      setShowDeleteConfirm(null);
      if (selectedFolder === folderId) {
        setSelectedFolder(null);
      }
    } catch (error) {
      console.error("[CK-2323] Error deleting clinic folder:", {
        errorCode: "CK-2323",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
      alert(error.message || "שגיאה במחיקת התיקייה");
    }
  };

  const handleUpdateFileDescription = async () => {
    if (!editingFile) return;

    try {
      await updateFile({
        clinicId,
        fileId: editingFile.id,
        fileData: {
          description: fileDescriptionForm.trim() || null,
        },
      }).unwrap();

      setEditingFile(null);
      setFileDescriptionForm("");
    } catch (error) {
      console.error("[CK-2324] Error updating clinic file:", {
        errorCode: "CK-2324",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleMoveFileToFolder = async (fileId, folderId) => {
    try {
      await updateFile({
        clinicId,
        fileId,
        fileData: { folderId },
      }).unwrap();
    } catch (error) {
      console.error("[CK-2325] Error moving clinic file:", {
        errorCode: "CK-2325",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      await deleteFile({
        clinicId,
        fileId,
        therapistId,
        therapistName,
      }).unwrap();
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error("[CK-2326] Error deleting clinic file:", {
        errorCode: "CK-2326",
        errorMessage: error.message,
        timestamp: new Date().toISOString(),
      });
    }
  };

  const handleDownload = (file) => {
    window.open(file.fileUrl, "_blank");
  };

  const handlePreview = (file) => {
    if (isFilePreviewable(file.fileType)) {
      setShowFilePreview(file);
    } else {
      handleDownload(file);
    }
  };

  const handlePrint = (file) => {
    if (file.fileType === "application/pdf") {
      // For PDFs: open in hidden iframe and trigger print
      const iframe = printIframeRef.current;
      if (iframe) {
        iframe.src = file.fileUrl;
        iframe.onload = () => {
          try {
            iframe.contentWindow.print();
          } catch {
            // Cross-origin fallback: open in new window
            const printWindow = window.open(file.fileUrl, "_blank");
            if (printWindow) {
              printWindow.onload = () => printWindow.print();
            }
          }
        };
      }
    } else if (file.fileType.startsWith("image/")) {
      // For images: open a print-friendly window
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html dir="rtl">
          <head>
            <title>${file.fileName}</title>
            <style>
              body { margin: 0; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: white; }
              img { max-width: 100%; max-height: 100vh; object-fit: contain; }
              @media print { body { margin: 0; } img { max-width: 100%; max-height: 100%; } }
            </style>
          </head>
          <body>
            <img src="${file.fileUrl}" alt="${file.fileName}" onload="window.print(); window.close();" />
          </body>
          </html>
        `);
        printWindow.document.close();
      }
    } else {
      // For other files, just download
      handleDownload(file);
    }
  };

  const openEditFolder = (folder) => {
    setEditingFolder(folder);
    setFolderForm({ name: folder.name, color: folder.color || FOLDER_COLORS[0] });
  };

  const openEditFile = (file) => {
    setEditingFile(file);
    setFileDescriptionForm(file.description || "");
  };

  const FileIcon = ({ fileType, size = 24 }) => {
    const category = getFileTypeCategory(fileType);
    const Icon = FILE_ICONS[category] || File;
    return <Icon size={size} weight="duotone" />;
  };

  // Render storage usage bar
  const renderStorageUsage = () => {
    if (!storageUsage) return null;

    const { usedStorageInMb, allowedStorageInMbMaxPerClinic, percentUsed } = storageUsage;
    const isNearLimit = percentUsed > 80;

    return (
      <div className={`storage-usage-bar ${isNearLimit ? "warning" : ""}`}>
        <div className="storage-info">
          <span className="storage-label">שימוש באחסון (כולל תיקי מטופלים)</span>
          <span className="storage-values">
            {usedStorageInMb.toFixed(1)} / {allowedStorageInMbMaxPerClinic} MB
          </span>
        </div>
        <div className="storage-progress">
          <div
            className="storage-progress-fill"
            style={{ width: `${Math.min(100, percentUsed)}%` }}
          />
        </div>
        {isNearLimit && (
          <div className="storage-warning">
            <Warning size={14} weight="bold" />
            מתקרב למגבלת האחסון
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (isLoadingFiles || isLoadingFolders) {
    return (
      <div className="clinic-files-tab">
        <ContentLoader fill message="טוען קבצים..." />
      </div>
    );
  }

  return (
    <div className="clinic-files-tab">
      {/* Hidden iframe for printing PDFs */}
      <iframe
        ref={printIframeRef}
        style={{ display: "none" }}
        title="print-frame"
      />

      {/* Header */}
      <div className="files-header">
        <div className="files-header-title">
          <Folder size={22} weight="duotone" />
          <h3>התיקייה שלי</h3>
          <span className="files-count">
            {files.length} קבצים
          </span>
        </div>

        {!clinicProfile?.isSoloClinic && (
          <div className="privacy-tag privacy-personal">
            <Eye size={14} weight="regular" />
            <span>המידע כאן אישי — למטפלים אחרים בקליניקה אין גישה אליו</span>
          </div>
        )}
        <div className="files-header-right">
          <HelpButton featureKey="clinic_files" />
          <div className="files-header-actions">
            <button
              className="files-btn secondary"
              onClick={() => setShowFolderModal(true)}
            >
              <FolderPlus size={18} weight="bold" />
              תיקייה חדשה
            </button>

            {/* Camera Button - Mobile only */}
            {isMobile && (
              <button
                className="files-btn primary camera-btn"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera size={18} weight="bold" />
                צלם
              </button>
            )}

            {/* Gallery Button - Mobile only */}
            {isMobile && (
              <button
                className="files-btn primary gallery-btn"
                onClick={() => galleryInputRef.current?.click()}
              >
                <ImageSquare size={18} weight="bold" />
                גלריה
              </button>
            )}

            {/* Regular File Upload */}
            <button
              className="files-btn primary"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadSimple size={18} weight="bold" />
              {isMobile ? "קובץ" : "העלאת קובץ"}
            </button>

            {/* Hidden file inputs */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
            <input
              ref={galleryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>

      {/* Storage Usage */}
      {renderStorageUsage()}

      {/* Error Message */}
      {uploadError && (
        <div className="files-error">
          <Warning size={18} weight="bold" />
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)}>
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {/* Folders Section */}
      {folders.length > 0 && (
        <div className="folders-section">
          <div className="folders-header">
            <span>תיקיות</span>
          </div>
          <div className="folders-list">
            {/* Root folder option */}
            <button
              className={`folder-item ${selectedFolder === null ? "active" : ""}`}
              onClick={() => setSelectedFolder(null)}
            >
              <FolderOpen size={20} weight="duotone" />
              <span>כל הקבצים</span>
            </button>

            {folders.map((folder) => (
              <div
                key={folder.id}
                className={`folder-item ${selectedFolder === folder.id ? "active" : ""}`}
              >
                <button
                  className="folder-btn"
                  onClick={() => setSelectedFolder(folder.id)}
                >
                  <Folder
                    size={20}
                    weight="duotone"
                    style={{ color: folder.color || FOLDER_COLORS[0] }}
                  />
                  <span>{folder.name}</span>
                </button>
                <div className="folder-actions">
                  <button
                    className="folder-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditFolder(folder);
                    }}
                  >
                    <PencilSimple size={14} weight="bold" />
                  </button>
                  <button
                    className="folder-action-btn delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm({ type: "folder", item: folder });
                    }}
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Breadcrumb when in folder */}
      {selectedFolder && (
        <div className="files-breadcrumb">
          <button onClick={() => setSelectedFolder(null)}>
            <CaretLeft size={14} weight="bold" />
            חזרה לכל הקבצים
          </button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">
            {folders.find((f) => f.id === selectedFolder)?.name || "תיקייה"}
          </span>
        </div>
      )}

      {/* Drop Zone */}
      <div
        className={`files-drop-zone ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isUploading ? (
          <div className="drop-zone-uploading">
            <LoaderLoop size="md" />
            <span>מעלה קבצים...</span>
          </div>
        ) : files.length === 0 && !isDragging ? (
          <div className="drop-zone-empty">
            <EmptyDraw variant="files" />
            <h4>אין קבצים עדיין</h4>
            <p>גרור קבצים לכאן או לחץ על "העלאת קובץ"</p>
            <span className="drop-zone-hint">
              <Info size={14} />
              קבצים משותפים לכל המטפלים בקליניקה • גודל מקסימלי: {MAX_FILE_SIZE_MB}MB לקובץ
            </span>
          </div>
        ) : isDragging ? (
          <div className="drop-zone-dragging">
            <CloudArrowUp size={48} weight="duotone" />
            <h4>שחרר כדי להעלות</h4>
          </div>
        ) : (
          <div className="files-grid">
            {files.map((file) => (
              <div key={file.id} className="file-card">
                <div className="file-card-icon">
                  <FileIcon fileType={file.fileType} size={32} />
                </div>

                <div className="file-card-info">
                  <span className="file-name" title={file.fileName}>
                    {file.fileName}
                  </span>
                  {file.description && (
                    <span className="file-description">{file.description}</span>
                  )}
                  <div className="file-meta">
                    <span>{formatFileSize(file.fileSize)}</span>
                    <span className="meta-dot" />
                    <span>{formatDate(file.createdAt)}</span>
                  </div>
                  <div className="file-uploader">
                    העלה: {file.uploadedByName}
                  </div>
                </div>

                <div className="file-card-actions">
                  {isFilePreviewable(file.fileType) && (
                    <button
                      className="file-action-btn"
                      onClick={() => handlePreview(file)}
                      title="תצוגה מקדימה"
                    >
                      <Eye size={16} weight="bold" />
                    </button>
                  )}
                  <button
                    className="file-action-btn"
                    onClick={() => handleDownload(file)}
                    title="הורדה"
                  >
                    <DownloadSimple size={16} weight="bold" />
                  </button>
                  {(file.fileType === "application/pdf" || file.fileType.startsWith("image/")) && (
                    <button
                      className="file-action-btn"
                      onClick={() => handlePrint(file)}
                      title="הדפסה"
                    >
                      <Printer size={16} weight="bold" />
                    </button>
                  )}
                  <button
                    className="file-action-btn"
                    onClick={() => openEditFile(file)}
                    title="עריכת תיאור"
                  >
                    <PencilSimple size={16} weight="bold" />
                  </button>
                  <button
                    className="file-action-btn delete"
                    onClick={() => setShowDeleteConfirm({ type: "file", item: file })}
                    title="מחיקה"
                  >
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Folder Modal */}
      {(showFolderModal || editingFolder) && (
        <div className="files-modal-overlay" onClick={() => {
          setShowFolderModal(false);
          setEditingFolder(null);
          setFolderForm({ name: "", color: FOLDER_COLORS[0] });
        }}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>{editingFolder ? "עריכת תיקייה" : "תיקייה חדשה"}</h3>
              <ModalCloseButton onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setFolderForm({ name: "", color: FOLDER_COLORS[0] });
                }} />
            </div>

            <div className="files-modal-body">
              <div className="form-group">
                <label>שם התיקייה</label>
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="לדוגמה: חומרי הדרכה"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>צבע</label>
                <div className="color-picker">
                  {FOLDER_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`color-option ${folderForm.color === color ? "active" : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFolderForm((prev) => ({ ...prev, color }))}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => {
                  setShowFolderModal(false);
                  setEditingFolder(null);
                  setFolderForm({ name: "", color: FOLDER_COLORS[0] });
                }}
              >
                ביטול
              </button>
              <button
                className="files-btn primary"
                onClick={editingFolder ? handleUpdateFolder : handleCreateFolder}
                disabled={!folderForm.name.trim() || isCreatingFolder}
              >
                {isCreatingFolder ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <Check size={16} weight="bold" />
                )}
                {editingFolder ? "שמור" : "צור תיקייה"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit File Description Modal */}
      {editingFile && (
        <div className="files-modal-overlay" onClick={() => {
          setEditingFile(null);
          setFileDescriptionForm("");
        }}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>עריכת תיאור קובץ</h3>
              <ModalCloseButton onClick={() => {
                  setEditingFile(null);
                  setFileDescriptionForm("");
                }} />
            </div>

            <div className="files-modal-body">
              <div className="file-preview-info">
                <FileIcon fileType={editingFile.fileType} size={24} />
                <span>{editingFile.fileName}</span>
              </div>

              <div className="form-group">
                <label>תיאור (אופציונלי)</label>
                <textarea
                  value={fileDescriptionForm}
                  onChange={(e) => setFileDescriptionForm(e.target.value)}
                  placeholder="לדוגמה: טופס הסכמה מדעת - תבנית"
                  rows={3}
                />
              </div>

              {folders.length > 0 && (
                <div className="form-group">
                  <label>העבר לתיקייה</label>
                  <select
                    value={editingFile.folderId || ""}
                    onChange={(e) => handleMoveFileToFolder(editingFile.id, e.target.value || null)}
                  >
                    <option value="">ללא תיקייה</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => {
                  setEditingFile(null);
                  setFileDescriptionForm("");
                }}
              >
                ביטול
              </button>
              <button
                className="files-btn primary"
                onClick={handleUpdateFileDescription}
              >
                <Check size={16} weight="bold" />
                שמור
              </button>
            </div>
          </div>
        </div>
      )}

      {/* File Preview Modal */}
      {showFilePreview && (
        <div className="files-modal-overlay preview-overlay" onClick={() => setShowFilePreview(null)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <span className="preview-filename">{showFilePreview.fileName}</span>
              <div className="preview-actions">
                {(showFilePreview.fileType === "application/pdf" || showFilePreview.fileType.startsWith("image/")) && (
                  <button onClick={() => handlePrint(showFilePreview)}>
                    <Printer size={20} weight="bold" />
                    הדפסה
                  </button>
                )}
                <button onClick={() => handleDownload(showFilePreview)}>
                  <DownloadSimple size={20} weight="bold" />
                  הורדה
                </button>
                <ModalCloseButton onClick={() => setShowFilePreview(null)} />
              </div>
            </div>
            <div className="preview-content">
              {showFilePreview.fileType.startsWith("image/") ? (
                <img src={showFilePreview.fileUrl} alt={showFilePreview.fileName} />
              ) : showFilePreview.fileType === "application/pdf" ? (
                <iframe
                  src={showFilePreview.fileUrl}
                  title={showFilePreview.fileName}
                  width="100%"
                  height="100%"
                />
              ) : (
                <div className="preview-not-available">
                  <File size={64} weight="duotone" />
                  <p>תצוגה מקדימה לא זמינה לסוג קובץ זה</p>
                  <button
                    className="files-btn primary"
                    onClick={() => handleDownload(showFilePreview)}
                  >
                    <DownloadSimple size={18} weight="bold" />
                    הורד לצפייה
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="files-modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="files-modal delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="files-modal-header">
              <h3>
                {showDeleteConfirm.type === "folder"
                  ? "מחיקת תיקייה"
                  : "מחיקת קובץ"}
              </h3>
            </div>

            <div className="files-modal-body">
              <div className="delete-warning">
                <Trash size={32} weight="duotone" />
                <p>
                  {showDeleteConfirm.type === "folder"
                    ? `האם אתה בטוח שברצונך למחוק את התיקייה "${showDeleteConfirm.item.name}"?`
                    : `האם אתה בטוח שברצונך למחוק את הקובץ "${showDeleteConfirm.item.fileName}"?`}
                </p>
                {showDeleteConfirm.type === "file" && (
                  <span className="delete-note">פעולה זו לא ניתנת לשחזור</span>
                )}
              </div>
            </div>

            <div className="files-modal-footer">
              <button
                className="files-btn secondary"
                onClick={() => setShowDeleteConfirm(null)}
              >
                ביטול
              </button>
              <button
                className="files-btn danger"
                onClick={() => {
                  if (showDeleteConfirm.type === "folder") {
                    handleDeleteFolder(showDeleteConfirm.item.id);
                  } else {
                    handleDeleteFile(showDeleteConfirm.item.id);
                  }
                }}
                disabled={isDeleting || isDeletingFolder}
              >
                {(isDeleting || isDeletingFolder) ? (
                  <LoaderLoop size="xs" />
                ) : (
                  <Trash size={16} weight="bold" />
                )}
                מחק
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClinicFilesTab;
